import { useEffect, useState } from 'react'
import { Text, View } from 'react-native'
import { useTranslation } from 'react-i18next'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { IdCard, User } from 'lucide-react-native'
import { colors } from '@chillout/core'
import { Button, InlineAlert } from '@chillout/ui'
import { AdminModal, ModalActions } from '../../../shared/Modal'
import { SuccessIcon } from '../../../shared/SuccessIcon'
import { SuccessInfoRow } from '../../../shared/SuccessInfoRow'
import { CopyModalActions } from '../../../shared/CopyModalActions'
import { CashierConfirmCard } from './CashierConfirmCard'
import { EditCashierForm } from './EditCashierForm'
import { useUpdateCashier } from './useUpdateCashier'
import { useResetCashierPin } from './useResetCashierPin'
import { useReactivateCashier } from './useReactivateCashier'
import { updateCashierSchema } from '../cashierSchemas'
import { useCopyWithFeedback } from '../../useCopyWithFeedback'
import { PinCard } from '../PinCard'
import type { Cashier } from '../useCashiers'

type Step = 'form' | 'deactivateConfirm' | 'resetConfirm' | 'pin'
type PinSource = 'reset' | 'reactivate'

interface EditCashierModalProps {
  cashier: Cashier | null
  onClose: () => void
}

export function EditCashierModal({ cashier, onClose }: EditCashierModalProps) {
  const { t } = useTranslation()
  const [step, setStep] = useState<Step>('form')
  const [pinSource, setPinSource] = useState<PinSource>('reset')
  const [localStatus, setLocalStatus] = useState<'active' | 'inactive' | null>(null)

  const saveMutation = useUpdateCashier()
  const deactivateMutation = useUpdateCashier()
  const resetPinMutation = useResetCashierPin()
  const reactivateMutation = useReactivateCashier()

  const { control, handleSubmit, reset, watch } = useForm({
    resolver: zodResolver(updateCashierSchema(t)),
    defaultValues: { name: '', email: '', role: 'staff' as const },
  })

  const effectiveStatus = localStatus ?? cashier?.status
  const isInactive = effectiveStatus === 'inactive'

  useEffect(() => {
    if (cashier) {
      reset({ name: cashier.name, email: cashier.email ?? '', role: cashier.role })
      setStep('form')
      setLocalStatus(null)
      saveMutation.reset()
      deactivateMutation.reset()
      resetPinMutation.reset()
      reactivateMutation.reset()
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cashier?.id])

  const pin = resetPinMutation.data?.pin ?? reactivateMutation.data?.pin ?? ''
  const { copied, copy: handleCopy } = useCopyWithFeedback(pin)

  const onSubmit = handleSubmit(({ name, email, role }) => {
    if (!cashier || !effectiveStatus) return
    saveMutation.mutate(
      { id: cashier.id, name: name.trim(), email: email.trim() || null, role, status: effectiveStatus },
      { onSuccess: onClose },
    )
  })

  function handleConfirmDeactivate() {
    if (!cashier) return
    deactivateMutation.mutate(
      { id: cashier.id, name: cashier.name, email: cashier.email, role: cashier.role, status: 'inactive' },
      { onSuccess: onClose },
    )
  }

  function handleReactivate() {
    if (!cashier) return
    reactivateMutation.mutate(
      { cashierId: cashier.id },
      {
        onSuccess: () => {
          setLocalStatus('active')
          setPinSource('reactivate')
          setStep('pin')
        },
      },
    )
  }

  function handleResetPin() {
    if (!cashier) return
    resetPinMutation.mutate(
      { cashierId: cashier.id },
      {
        onSuccess: () => {
          setPinSource('reset')
          setStep('pin')
        },
      },
    )
  }

  const pinTitle = pinSource === 'reactivate'
    ? t('operations.editCashier.reactivatePinTitle')
    : t('operations.editCashier.pinTitle')

  const titles: Record<Step, string> = {
    form: t('operations.editCashier.title'),
    deactivateConfirm: t('operations.editCashier.makeInactiveButton'),
    resetConfirm: t('operations.editCashier.resetPinTitle'),
    pin: pinTitle,
  }

  const subtitles: Record<Step, string | undefined> = {
    form: t('operations.editCashier.subtitle'),
    deactivateConfirm: t('operations.editCashier.deactivateConfirmDescription'),
    resetConfirm: t('operations.editCashier.resetPinDescription'),
    pin: undefined,
  }

  const secondaryAction = isInactive ? (
    <Button
      label={t('operations.editCashier.reactivateButton')}
      loadingLabel={t('operations.editCashier.reactivatingButton')}
      variant="secondary"
      className="flex-1"
      loading={reactivateMutation.isPending}
      onPress={handleReactivate}
    />
  ) : (
    <Button
      label={t('operations.editCashier.makeInactiveButton')}
      variant="secondary"
      className="flex-1"
      onPress={() => setStep('deactivateConfirm')}
    />
  )

  return (
    <AdminModal
      visible={cashier !== null}
      onClose={onClose}
      closeAccessibilityLabel={t('operations.editCashier.closeAccessibilityLabel')}
      title={titles[step]}
      subtitle={subtitles[step]}
      icon={step === 'pin' ? <SuccessIcon /> : undefined}
      actions={
        step === 'pin' ? (
          <CopyModalActions
            copied={copied}
            copyLabel={t('operations.editCashier.pinCopyButton')}
            copiedLabel={t('operations.editCashier.pinCopiedButton')}
            doneLabel={t('common.done')}
            onCopy={handleCopy}
            onDone={onClose}
          />
        ) : step === 'deactivateConfirm' ? (
          <ModalActions>
            <Button
              label={t('operations.editCashier.resetPinBackButton')}
              variant="secondary"
              className="flex-1"
              onPress={() => { setStep('form'); deactivateMutation.reset() }}
            />
            <Button
              label={t('operations.editCashier.deactivateButton')}
              loadingLabel={t('operations.editCashier.deactivatingButton')}
              variant="primary"
              className="flex-1"
              loading={deactivateMutation.isPending}
              onPress={handleConfirmDeactivate}
            />
          </ModalActions>
        ) : step === 'resetConfirm' ? (
          <ModalActions>
            <Button
              label={t('operations.editCashier.resetPinBackButton')}
              variant="secondary"
              className="flex-1"
              onPress={() => { setStep('form'); resetPinMutation.reset() }}
            />
            <Button
              label={t('operations.editCashier.resetPinConfirmButton')}
              loadingLabel={t('operations.editCashier.resetPinResettingButton')}
              variant="primary"
              className="flex-1"
              loading={resetPinMutation.isPending}
              onPress={handleResetPin}
            />
          </ModalActions>
        ) : (
          <ModalActions>
            {secondaryAction}
            <Button
              label={t('operations.editCashier.submitButton')}
              loadingLabel={t('operations.editCashier.savingButton')}
              variant="primary"
              className="flex-1"
              loading={saveMutation.isPending}
              onPress={onSubmit}
            />
          </ModalActions>
        )
      }
    >
      {step === 'pin' ? (
        <View className="mt-4 gap-4">
          <SuccessInfoRow
            left={{ icon: <User size={20} color={colors.text.secondary} strokeWidth={2} />, label: cashier?.name ?? watch('name') }}
            right={{ icon: <IdCard size={20} color={colors.text.secondary} strokeWidth={2} />, label: t(`operations.roles.${cashier?.role ?? watch('role')}`) }}
          />
          <Text className="text-sm font-normal text-secondary">
            {t('operations.editCashier.pinDescription')}
          </Text>
          <PinCard pin={pin} />
        </View>
      ) : step === 'deactivateConfirm' ? (
        <View className="mt-5 gap-4">
          <CashierConfirmCard
            name={cashier?.name ?? watch('name')}
            roleLabel={t(`operations.roles.${cashier?.role ?? watch('role')}`)}
          />
          {deactivateMutation.isError && (
            <InlineAlert message={t('operations.editCashier.deactivateError')} />
          )}
        </View>
      ) : step === 'resetConfirm' ? (
        <View className="mt-5 gap-4">
          <CashierConfirmCard
            name={cashier?.name ?? watch('name')}
            roleLabel={t(`operations.roles.${cashier?.role ?? watch('role')}`)}
          />
          {resetPinMutation.isError && (
            <InlineAlert message={t('operations.editCashier.resetPinError')} />
          )}
        </View>
      ) : (
        <View className="mt-6 gap-5">
          <EditCashierForm
            control={control}
            effectiveStatus={effectiveStatus}
            isInactive={isInactive}
            onResetPin={() => setStep('resetConfirm')}
          />
          {reactivateMutation.isError && (
            <InlineAlert message={t('operations.editCashier.statusChangeError')} />
          )}
          {saveMutation.isError && (
            <InlineAlert message={t('operations.editCashier.submitError')} />
          )}
        </View>
      )}
    </AdminModal>
  )
}